import { GlobalState } from '../../global-state/global-state';
import {
  STATE_TO_LOCATION_LABEL,
  ALL_DISPLAY_STATES,
  LABEL_TO_STATE,
  isAvailableForState,
  getZoneAvailability,
} from '../../utils/stock-availability';
import { STATE_EVENTS } from '../../utils/state-events';

function parseStockJSON(node) {
  if (!node) return null;
  try { return JSON.parse(node.textContent); } catch (e) { return null; }
}

function getCurrentVariantId(form) {
  if (!form) return null;
  const input = form.querySelector('input[name="id"]');
  return input ? input.value : null;
}

function setIndicator(indicator, locationEl, { mode, stateCode }) {
  if (!indicator) return;
  indicator.classList.remove(
    'warehouse-location__status--available',
    'warehouse-location__status--unavailable',
    'warehouse-location__status--no-state'
  );

  const loc = STATE_TO_LOCATION_LABEL[stateCode] || stateCode;
  if (mode === 'no-state') {
    indicator.innerHTML = 'Set Your State To See Availability';
    if (locationEl) locationEl.innerHTML = '';
    indicator.classList.add('warehouse-location__status--no-state');
  } else if (mode === 'in-stock') {
    indicator.innerHTML = '<span class="warehouse-location__dot warehouse-location__dot--available"></span> Available';
    indicator.classList.add('warehouse-location__status--available');
    if (locationEl) locationEl.innerHTML = `Location: <strong>${loc}</strong>`;
  } else if (mode === 'oos') {
    indicator.innerHTML = '<span class="warehouse-location__dot warehouse-location__dot--unavailable"></span> Unavailable';
    indicator.classList.add('warehouse-location__status--unavailable');
    if (locationEl) locationEl.innerHTML = `Location: <strong>${loc}</strong>`;
  }
}

function setAtcState(button, { mode }) {
  if (!button) return;
  const textEl = button.querySelector('#AddToCart--text');
  const notifyBtn = document.querySelector('.klaviyo-bis-trigger');

  // #679 -- defer to the size-selection guard when an explicit size pick is
  // required but no size has been chosen yet. Without this short-circuit,
  // #417's state-availability override would unconditionally rewrite the
  // button text to "Add to Bag" / "Out of Stock" and stomp the
  // "Please Select a Size" label rendered server-side by product-form.liquid.
  const form = document.querySelector('[data-product-form]');
  if (form?.dataset?.requiresExplicitSize === 'true') {
    const sizeSelects = document.querySelectorAll('select[data-requires-selection="true"]');
    const sizeMissing = Array.from(sizeSelects).some((sel) => !sel.value);
    if (sizeMissing) {
      button.setAttribute('disabled', 'disabled');
      if (textEl) textEl.textContent = 'Please Select a Size';
      if (notifyBtn) {
        notifyBtn.classList.remove('is-forced-visible', 'is-state-available');
        notifyBtn.removeEventListener('click', openKlaviyoBis);
      }
      return;
    }
  }

  if (mode === 'no-state') {
    button.setAttribute('disabled', 'disabled');
    if (textEl) textEl.textContent = 'Add to Bag';
    if (notifyBtn) {
      notifyBtn.classList.remove('is-forced-visible', 'is-state-available');
      notifyBtn.removeEventListener('click', openKlaviyoBis);
    }
  } else if (mode === 'oos') {
    button.setAttribute('disabled', 'disabled');
    if (textEl) textEl.textContent = 'Out of Stock';
    if (notifyBtn) {
      notifyBtn.classList.add('is-forced-visible');
      notifyBtn.classList.remove('is-state-available');
      notifyBtn.removeEventListener('click', openKlaviyoBis);
      notifyBtn.addEventListener('click', openKlaviyoBis);
    }
  } else {
    button.removeAttribute('disabled');
    if (textEl) textEl.textContent = 'Add to Bag';
    if (notifyBtn) {
      notifyBtn.classList.remove('is-forced-visible');
      notifyBtn.classList.add('is-state-available');
      notifyBtn.removeEventListener('click', openKlaviyoBis);
    }
  }
}

function openKlaviyoBis(e) {
  e.preventDefault();
  e.stopImmediatePropagation(); // Prevent Klaviyo's native handler from firing
  if (window.KlaviyoOnsiteSdk) {
    const variantId = getCurrentVariantId(document.querySelector('[data-product-form]'));
    window.KlaviyoOnsiteSdk.openForm(variantId ? { variantId } : undefined);
  }
}

function renderDetailsList(listEl, zones) {
  if (!listEl) return;
  listEl.innerHTML = '';
  for (const label of ALL_DISPLAY_STATES) {
    const available = zones[label];
    const li = document.createElement('li');
    li.className = 'warehouse-location__list-item';
    const dotClass = available ? 'warehouse-location__dot--available' : 'warehouse-location__dot--unavailable';
    const statusClass = available ? 'warehouse-location__list-status--available' : 'warehouse-location__list-status--unavailable';
    li.innerHTML = `<span class="warehouse-location__dot ${dotClass}"></span>` +
      `<span class="warehouse-location__list-status ${statusClass}">${available ? 'Available' : 'Unavailable'}</span>` +
      `<span class="warehouse-location__list-label">${label}</span>`;
    li.style.cursor = 'pointer';
    li.addEventListener('click', () => {
      const stateCode = LABEL_TO_STATE[label];
      if (!stateCode) return;
      // Ask whichever state-selector instance is mounted to apply this state.
      // `tena:select-state` is the inverse of `tena:state-selected`: callers
      // dispatch this to request a change, the selector then dispatches
      // `tena:state-selected` once the change is committed.
      window.dispatchEvent(new CustomEvent(STATE_EVENTS.SELECT, {
        detail: { state: stateCode },
      }));
    });
    listEl.appendChild(li);
  }
}

function evaluate(ctx) {
  const { stockData, form, atcButton, indicator, locationEl, listEl } = ctx;
  const variantId = getCurrentVariantId(form);
  const effectiveState = GlobalState.getEffectiveState();

  const zones = getZoneAvailability(stockData, variantId);
  renderDetailsList(listEl, zones);

  if (!effectiveState) {
    setIndicator(indicator, locationEl, { mode: 'no-state' });
    setAtcState(atcButton, { mode: 'no-state' });
    return;
  }

  const stateAvail = isAvailableForState(stockData, variantId, effectiveState);

  if (stateAvail === null) {
    // No per-state data for this variant — fall back to national availability
    setIndicator(indicator, locationEl, { mode: 'in-stock', stateCode: effectiveState });
    setAtcState(atcButton, { mode: 'available' });
  } else if (stateAvail === true) {
    setIndicator(indicator, locationEl, { mode: 'in-stock', stateCode: effectiveState });
    setAtcState(atcButton, { mode: 'available' });
  } else {
    setIndicator(indicator, locationEl, { mode: 'oos', stateCode: effectiveState });
    setAtcState(atcButton, { mode: 'oos' });
  }

  window.dispatchEvent(new CustomEvent('tena:availability-changed', {
    detail: { variantId, state: effectiveState, available: stateAvail },
  }));
}

export default function ProductStockByState() {
  const wrapper = document.querySelector('[data-stock-by-state-wrapper]');
  if (!wrapper) return;
  const jsonNode = wrapper.querySelector('[data-stock-by-state]');
  const indicator = wrapper.querySelector('[data-stock-indicator]');
  const locationEl = wrapper.querySelector('[data-stock-location]');
  const listEl = wrapper.querySelector('[data-stock-list]');
  const toggleBtn = wrapper.querySelector('[data-stock-toggle]');
  const detailsEl = wrapper.querySelector('[data-stock-details]');
  const form = document.querySelector('[data-product-form]');
  const atcButton = document.querySelector('#AddToCart');
  if (!jsonNode || !form || !atcButton) return;

  const infoBtn = wrapper.querySelector('.warehouse-location__info');
  if (infoBtn) {
    infoBtn.addEventListener('click', () => {
      let tip = wrapper.querySelector('.warehouse-location__tooltip');
      if (tip) {
        tip.remove();
        return;
      }
      tip = document.createElement('div');
      tip.className = 'warehouse-location__tooltip';
      tip.textContent = 'This product ships by state. It needs to be in stock for your state for delivery.';
      infoBtn.appendChild(tip);
      const dismiss = (e) => {
        if (!tip.contains(e.target) && !infoBtn.contains(e.target)) {
          tip.remove();
          document.removeEventListener('click', dismiss);
        }
      };
      setTimeout(() => document.addEventListener('click', dismiss), 0);
    });
  }

  const stockData = parseStockJSON(jsonNode);
  const ctx = { stockData, form, atcButton, indicator, locationEl, listEl };

  evaluate(ctx);

  const summaryEl = wrapper.querySelector('[data-warehouse-summary]');
  if (detailsEl) {
    const warehouseEl = wrapper.querySelector('[data-warehouse-location]');
    detailsEl.removeAttribute('style');
    const toggle = () => {
      const open = detailsEl.classList.contains('is-open');
      detailsEl.classList.toggle('is-open', !open);
      if (toggleBtn) toggleBtn.classList.toggle('is-open', !open);
      if (warehouseEl) warehouseEl.classList.toggle('is-expanded', !open);
    };
    if (summaryEl) {
      summaryEl.style.cursor = 'pointer';
      summaryEl.addEventListener('click', (e) => {
        if (e.target.closest('.warehouse-location__info')) return;
        toggle();
      });
    }
  }

  window.addEventListener(STATE_EVENTS.SELECTED, () => evaluate(ctx));
  window.addEventListener(STATE_EVENTS.CLEARED, () => evaluate(ctx));
  window.addEventListener(STATE_EVENTS.DETECTED, () => evaluate(ctx));

  const variantIdInput = form.querySelector('input[name="id"]');
  if (variantIdInput && window.MutationObserver) {
    const mo = new MutationObserver(() => evaluate(ctx));
    mo.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });
  }
}
